import React from 'react';
import { alpha } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  MoreVert as MoreIcon,
  AccountCircle
} from '@mui/icons-material';
import FolderIcon from '@mui/icons-material/Folder';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import api from '../api.js';
import ComplisenseLogo from './ComplisenseLogo';

export default function PrimarySearchAppBar({ handleDrawerToggle }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const [notificationCount, setNotificationCount] = React.useState(0);
  const navigate = useNavigate();

  React.useEffect(() => {
    let ignore = false;
    async function fetchCount() {
      try {
        const { data } = await api.get('/notifications/unread-count', {
          withCredentials: true
        });
        if (!ignore) setNotificationCount(data.count);
      } catch (err) {
        console.error('Count fetch failed', err);
      }
    }
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => {
      ignore = true;
      clearInterval(timer);
    };
  }, []);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const iconButtonSx = {
    color: 'rgba(255,255,255,0.9)',
    '&:hover': { backgroundColor: alpha('#fff', 0.1) }
  };

  const handleProfileMenuOpen = React.useCallback(
    e => setAnchorEl(e.currentTarget),
    []
  );
  const handleMobileMenuOpen = React.useCallback(
    e => setMobileMoreAnchorEl(e.currentTarget),
    []
  );
  const handleMobileMenuClose = React.useCallback(
    () => setMobileMoreAnchorEl(null),
    []
  );
  const handleMenuClose = React.useCallback(() => {
    setAnchorEl(null);
    handleMobileMenuClose();
  }, [handleMobileMenuClose]);

  const handleLogout = React.useCallback(async () => {
    try {
      const { data } = await api.post(
        `auth/logout`,
        {},
        { withCredentials: true }
      );
      if (data) {
        localStorage.removeItem('userRole');
        localStorage.removeItem('complisense:lastRoute');
        toast.success('Logged Out Successfully');
        navigate('/login');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  }, [navigate]);

  const handleGoProfile = React.useCallback(() => {
    handleMenuClose();
    navigate('/profile');
  }, [handleMenuClose, navigate]);

  const handleLogoutClick = React.useCallback(() => {
    handleMenuClose();
    handleLogout();
  }, [handleMenuClose, handleLogout]);

  const handleGoDrive = React.useCallback(() => navigate('/drive'), [navigate]);
  const handleGoNotifications = React.useCallback(
    () => navigate('/notifications'),
    [navigate]
  );

  const menuId = 'primary-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      slotProps={{
        paper: {
          sx: theme => ({
            mt: 1,
            minWidth: 240,
            borderRadius: 2,
            overflow: 'visible',
            boxShadow: '0px 12px 32px rgba(0,0,0,0.28)',
            border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
            backgroundImage: 'none',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 18,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              borderLeft: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
              borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`
            }
          })
        }
      }}
      MenuListProps={{
        sx: {
          py: 0.5,
          '& .MuiMenuItem-root': {
            borderRadius: 1.25,
            mx: 0.75,
            my: 0.25
          }
        }
      }}
    >
      <Box sx={{ px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Account
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Manage your profile & session
        </Typography>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={handleGoProfile}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <PersonOutlineIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="My Profile"
          primaryTypographyProps={{ fontWeight: 600 }}
        />
      </MenuItem>
      <MenuItem
        onClick={handleLogoutClick}
        sx={{
          '&:hover': {
            bgcolor: theme => alpha(theme.palette.error.main, 0.08)
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="Logout"
          primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }}
        />
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
      slotProps={{
        paper: {
          sx: theme => ({
            mt: 1,
            minWidth: 240,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0px 12px 32px rgba(0,0,0,0.28)',
            border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
            backgroundImage: 'none'
          })
        }
      }}
      MenuListProps={{
        sx: {
          py: 0.5,
          '& .MuiMenuItem-root': {
            borderRadius: 1.25,
            mx: 0.75,
            my: 0.25
          }
        }
      }}
    >
      <MenuItem
        onClick={() => {
          handleMobileMenuClose();
          handleGoDrive();
        }}
      >
        <Tooltip title="My Drive" placement="right">
          <IconButton size="large" sx={iconButtonSx}>
            <FolderIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="body2" sx={{ ml: 1 }}>
          My Drive
        </Typography>
      </MenuItem>

      <MenuItem
        onClick={() => {
          handleMobileMenuClose();
          handleGoNotifications();
        }}
      >
        <Tooltip title="Notifications" placement="right">
          <IconButton size="large" sx={iconButtonSx}>
            <Badge badgeContent={notificationCount} max={99} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <Typography variant="body2" sx={{ ml: 1 }}>
          Notifications
        </Typography>
      </MenuItem>

      <Divider sx={{ my: 0.5, mx: 0.75 }} />

      <MenuItem
        onClick={e => {
          handleMobileMenuClose();
          handleProfileMenuOpen(e);
        }}
      >
        <IconButton size="large" sx={iconButtonSx}>
          <AccountCircle />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1 }}>
          Profile
        </Typography>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <ToastContainer position="top-center" autoClose={5000} />
      <AppBar
        position="fixed"
        sx={{
          background: 'linear-gradient(90deg, #05172E 0%, #242541 100%)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, ...iconButtonSx }}
            aria-label="open drawer"
          >
            <MenuIcon />
          </IconButton>

          <Link
            to="/dashboard"
            style={{ textDecoration: 'none', color: 'inherit', flexGrow: 1, display: 'flex', alignItems: 'center' }}
          >
            <ComplisenseLogo
              variant="wordmark"
              size="small"
              iconSize={32}
              typographySx={{ color: 'rgba(255,255,255,0.98)', fontSize: { xs: '1rem', sm: '1.125rem' } }}
            />
          </Link>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <Tooltip title="My Drive">
              <IconButton onClick={handleGoDrive} sx={{ mx: 1, ...iconButtonSx }}>
                <FolderIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notifications">
              <IconButton
                onClick={handleGoNotifications}
                sx={{ mx: 1, ...iconButtonSx }}
              >
                <Badge badgeContent={notificationCount} max={99} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <IconButton
              edge="end"
              onClick={handleProfileMenuOpen}
              sx={{ ml: 1, ...iconButtonSx }}
              aria-controls={isMenuOpen ? menuId : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? 'true' : undefined}
            >
              <AccountCircle />
            </IconButton>
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={iconButtonSx}
              aria-controls={isMobileMenuOpen ? mobileMenuId : undefined}
              aria-haspopup="true"
              aria-expanded={isMobileMenuOpen ? 'true' : undefined}
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {renderMobileMenu}
      {renderMenu}
    </Box>
  );
}
